import { supabase } from '@/lib/supabase';

/**
 * Tasks Service - Handles all task-related operations with Supabase
 */

export const tasksService = {
  /**
   * Get all tasks, optionally sorted
   * @param {string} sortBy - Sort field (e.g., '-created_date' for descending)
   * @returns {Promise<Array>}
   */
  async list(sortBy = '-created_date') {
    const ascending = !sortBy.startsWith('-');
    const field = sortBy.replace('-', '');
    
    const { data, error } = await supabase
      .from('tasks')
      .select('*')
      .order(field, { ascending });

    if (error) throw error;
    return data || [];
  },

  /**
   * Filter tasks by criteria
   * @param {Object} filters - Filter criteria (e.g., { contractor_email: 'email', status: 'assigned' })
   * @param {string} sortBy - Sort field
   * @returns {Promise<Array>}
   */
  async filter(filters = {}, sortBy = '-created_date') {
    const ascending = !sortBy.startsWith('-');
    const field = sortBy.replace('-', '');
    
    let query = supabase.from('tasks').select('*');

    // Apply filters
    Object.entries(filters).forEach(([key, value]) => {
      query = query.eq(key, value);
    });

    // Apply sorting
    query = query.order(field, { ascending });

    const { data, error } = await query;

    if (error) throw error;
    return data || [];
  },

  /**
   * Get a single task by ID
   * @param {string} id - Task ID
   * @returns {Promise<Object>}
   */
  async getById(id) {
    const { data, error } = await supabase
      .from('tasks')
      .select('*')
      .eq('id', id)
      .single();

    if (error) throw error;
    return data;
  },

  /**
   * Create a new task
   * @param {Object} taskData - Task data
   * @returns {Promise<Object>}
   */
  async create(taskData) {
    const { data, error } = await supabase
      .from('tasks')
      .insert([{
        ...taskData,
        // created_date and status use database defaults
      }])
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  /**
   * Update a task
   * @param {string} id - Task ID
   * @param {Object} updates - Fields to update
   * @returns {Promise<Object>}
   */
  async update(id, updates) {
    const { data, error } = await supabase
      .from('tasks')
      .update({
        ...updates,
        updated_at: new Date().toISOString()
      })
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  /**
   * Delete a task
   * @param {string} id - Task ID
   * @returns {Promise<void>}
   */
  async delete(id) {
    const { error } = await supabase
      .from('tasks')
      .delete()
      .eq('id', id);

    if (error) throw error;
  }
};
